// React
import { useRef } from 'react';
// Styles
import styles from './Charts.module.css'
// Components
import Button from 'react-bootstrap/Button';
import RBTable from 'react-bootstrap/Table'; // alias to avoid conflict
import LoadingChart from './LoadingChart';
import NoDataChart from './NoDataChart';
import DownloadIcon from '../Icons/DownloadIcon';
// Helpers
import { 
  downloadTableToCSV,
  checkDataArrayForAllNullValues 
} from './ChartHelpers';

/* -------------------------------------------------------------------------- */
/*                                   TABLE                                    */
/* -------------------------------------------------------------------------- */

/**
 * Component for a standard table of data.
 * @component
 * @param {Object} props - The component accepts props.
 * @param {Objects[]} props.labelArray - Length of this array should be equal to the number of categories to be included on the chart. For example, an array to show two sets of bars for two sets of data like expenditure and revenue would be [
  {
    label: 'Expenditure',
    xKey: 'year',
    yKey: 'values.expenditure'
  },
  {
    label: 'Revenue',
    xKey: 'year',
    yKey: 'values.revenue'
  }
].
 * @param {Objects[]} props.dataArray - An array of chart data. Must be configured in the form [...{xAxisKey: string, values:{...value: number} }]
 * @param {boolean} props.dashboardLoading - Dashboard loading spinner state.
 * @param {string} [props.chartTitle] - The title of the chart [default='No Title Provided']. 
 * @param {object | boolean} [props.filterText] - Object of text indicating the filters chosen. Default is false.
 * @returns A dashboard bar chart component in a card.
 */
const Table = ({
  labelArray,
  dataArray,
  dashboardLoading,
  chartTitle = 'No Title Provided',
  filterText = false
}) => {

  const chartRef = useRef(null);

  /* --------------------------------- Loading -------------------------------- */
  
  if (dashboardLoading) {
    return <LoadingChart/>
  }
  
  /* ----------------------------- Check Null Data ---------------------------- */
  
  const dataOk = checkDataArrayForAllNullValues(dataArray);
  if (chartTitle === null || labelArray === null || dataArray === null || dataOk === false) {
    return <NoDataChart/>
  }

  try {
    
    /* ---------------------------- Transform Data ---------------------------- */
    
    const flattened = Array.isArray(dataArray)
      ? dataArray.map(item => ({
          label: item?.label ?? '',
          ...(item?.values ?? {})
        }))
      : [];

    const valueKeysSet = new Set();
    for (const row of flattened) {
      for (const key of Object.keys(row)) {
        if (key !== 'label') valueKeysSet.add(key);
      }
    }
    const columns = ['label', ...Array.from(valueKeysSet)];

    //console.log(dataArray);

    /* -------------------------------- Component ------------------------------- */
    
    return (
      <div className={styles.chartBoxHeight}>
        <div className={`${styles.chartHeight} overflow-auto`}>

          {/* React-Bootstrap Table */}

            <RBTable // container needs a fixed width for overflow to work
              ref={chartRef}
              striped
              hover
              size="sm"
              className="mb-0 align-middle"
              responsive
            >
              <thead>
                <tr>
                  {columns.map(col => (
                    <th key={col} scope="col">
                      {col === 'label' ? 'Label' : col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {flattened.length === 0 ? (
                  <tr>
                    <td colSpan={columns.length} className="text-center text-muted">
                      No rows to display
                    </td>
                  </tr>
                ) : (
                  flattened.map((row, idx) => (
                    <tr key={idx}>
                      {columns.map(col => (
                        <td key={col}>
                          {row[col] == null ? '' : String(row[col])}
                        </td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
            </RBTable>


        </div>

        {/* Export */}
        <div className={`text-end ${styles.downloadButtonDivHeight}`}>
          <Button
            variant="outline-primary"
            size='sm'
            className={`mt-2 fw-bold icon-link icon-link-hover ${styles.downloadButton}`}
            onClick={(event) => downloadTableToCSV(event, chartRef, chartTitle)}
          >
            <DownloadIcon width={16}/>
            Export Data
          </Button>
        </div>
      </div>
    );

  /* ---------------------------------- Error --------------------------------- */
  } catch (error) {
    console.log(error);
    return <NoDataChart message={`Chart Error: ${error.message}`}/>
  }
}

export default Table;

